import { FollowOnPortfolioDTO } from "src/app/domain/follow-on/component/dto/FollowOnPortfolioDTO";
import { Rows } from "../../interfaces";
import { formats, getCell, withCellStyles } from "../../utils";
import { FollowOnModelDtoForExcel } from "./FollowOnModelTabletemplate";
import { left, leftBold } from "src/app/util/excelStyles";
import { boldGrayBgLeft, boldLightGrayBgLeft } from "../comparison/mapping";
import { boldLightGrayBg } from "../financial-statement/capital-account/styles";

export const getPreparedData = (data: FollowOnModelDtoForExcel) => {
  const newData: Rows = [];
  const active = data.data.active;
  const realized = data.data.realized;
  newData.push(data.upperHeaders);
  const row = [];
  data.headersForDisplay.forEach((header) => (header === '' ? row.push(null) : row.push(withCellStyles(header, boldLightGrayBg))));
  newData.push(row);
  if (active.models.length) {
    const nameActiveRow = [];
    nameActiveRow.push(withCellStyles('Active', leftBold));
    newData.push(nameActiveRow);
    const activeRows = getActiveOrRealizedRows(active.models, 'active', data.headersForLogic);
    newData.push(...activeRows);
    const subTotalActive = getSubTotalRows(data.headersForLogic, 'Active', 4);
    newData.push(...subTotalActive);
    // set marker of total active
    activeTotalRowIndex = currentRowIndex;
    currentRowIndex++;
  }
  if (realized.models.length) {
    const nameRealizedRow = [];
    nameRealizedRow.push(withCellStyles('Realized', leftBold));
    newData.push(nameRealizedRow);
    currentRowIndex++;
    const realizedRows = getActiveOrRealizedRows(realized.models, 'realized', data.headersForLogic);
    newData.push(...realizedRows);
    // 4 here start row and + 2 need to sum for row totalActive and row realized
    const startRowForRealizedSubTotal = active.models?.length ? 4 + active.models?.length + 2 : 4;
    const subTotalRealized = getSubTotalRows(data.headersForLogic, 'Realized', startRowForRealizedSubTotal);
    newData.push(...subTotalRealized);
    // set marker for total realized
    realizedTotalRowIndex = currentRowIndex;
    currentRowIndex++;
  }
  if (active.models?.length && realized.models?.length) {
    const grandTotalRow = getGrandTotalRow(data.headersForLogic);
    newData.push(...grandTotalRow);
  }
  resetRowIndexes();
  return newData;
};

const getActiveOrRealizedRows = (models: FollowOnPortfolioDTO[], status: string, headers: string[]) => {
  const rows: Rows = [];
  const mappedToHeaders = models.map((model) => getNewModelMapping(model));
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], left));
    }
    rows.push(row);
  }
  return rows;
};

const getNewModelMapping = (model: FollowOnPortfolioDTO) => {
  const body = {
    'Company': model.name,
    'Investment': getCell({
      v: model.perfPredict.current.investment,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Empty': null,
    'Fair value': getCell({
      v: model.perfPredict.current.fairValue,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC': getCell({
      f: getMoicForSubTotal('D', 'C', currentRowIndex),
      z: formats.multiplier,
      t: 'n',
    }),
    'Comp.value': getCell({
      v: dashInsteadOfNull(model.currentCompValue),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Empty(0)': null,
    'Investment(0)': getCell({
      f: getDiffFormuaForDelta('L', 'C'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(0)': getCell({
      f: getDiffFormuaForDelta('M', 'D'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(0)': getCell({
      f: getDiffFormuaForDelta('N', 'E'),
      z: formats.multiplier,
      t: 'n',
    }),
    'Empty(1)': null,
    'Investment(1)': getCell({
      v: model.perfPredict.predict.investment,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(1)': getCell({
      v: model.perfPredict.predict.fairValue,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(1)': getCell({
      f: getMoicForSubTotal('M', 'L', currentRowIndex),
      z: formats.multiplier,
      t: 'n',
    }),
  };
  currentRowIndex++;
  return body;
};


const getSubTotalRows = (headers: string[], status: string, startRow: number) => {
  const rows: Rows = [];
  const mappedToHeaders = getSubTotalMapping(status, startRow);
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldLightGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

const getSubTotalMapping = (status: string, startRow: number) => {
  const body = {
    'Company': 'Total ' + status,
    'Empty': null,
    'Investment': getCell({
      f: getSumForSubTotal('C', startRow, (currentRowIndex - 1)),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value': getCell({
      f: getSumForSubTotal('D', startRow, (currentRowIndex - 1)),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC': getCell({
      f: getMoicForSubTotal('D', 'C', currentRowIndex), // currenRowIndex is now one row before
      z: formats.multiplier,
      t: 'n',
    }),
    'Comp.value': getCell({
      f: getSumForSubTotal('F', startRow, (currentRowIndex - 1)),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Empty(0)': null,
    'Investment(0)': getCell({
      f: getDiffFormuaForDelta('L', 'C'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(0)': getCell({
      f: getDiffFormuaForDelta('M', 'D'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(0)': getCell({
      f: getDiffFormuaForDelta('N', 'E'),
      z: formats.multiplier,
      t: 'n',
    }),
    'Empty(1)': null,
    'Investment(1)': getCell({
      f: getSumForSubTotal('L', startRow, currentRowIndex - 1),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(1)': getCell({
      f: getSumForSubTotal('M', startRow, currentRowIndex - 1),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(1)': getCell({
      f: getMoicForSubTotal('M', 'L', currentRowIndex),
      z: formats.multiplier,
      t: 'n',
    }),
  };
  return [body];
};

const getGrandTotalRow = (headers: string[]) => {
  const rows: Rows = [];
  const mappedToHeaders = getGrandTotalMapping();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

const getGrandTotalMapping = () => {
  const body = {
    'Company': 'Grand total',
    'Empty': null,
    'Investment': getCell({
      f: getSumForGrandTotal('C'),
      z: formats.thousandsSeparator,
      t: 'n'
    }),
    'Fair value': getCell({
      f: getSumForGrandTotal('D'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC': getCell({
      f: getMoicForSubTotal('D', 'C', currentRowIndex), // currenRowIndex is now one row before
      z: formats.multiplier,
      t: 'n'
    }),
    'Comp.value': getCell({
      f: getSumForGrandTotal('F'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Empty(0)': null,
    'Investment(0)': getCell({
      f: getDiffFormuaForDelta('L', 'C'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(0)': getCell({
      f: getDiffFormuaForDelta('M', 'D'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(0)': getCell({
      f: getDiffFormuaForDelta('N', 'E'),
      z: formats.multiplier,
      t: 'n',
    }),
    'Empty(1)': null,
    'Investment(1)': getCell({
      f: getSumForGrandTotal('L'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Fair value(1)': getCell({
      f: getSumForGrandTotal('M'),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'MOIC(1)': getCell({
      f: getMoicForSubTotal('M', 'L', currentRowIndex),
      z: formats.multiplier,
      t: 'n',
    }),
  };
  return [body];
};

const dashInsteadOfNull = (value) => value ? value : '-'

const resetRowIndexes = () => {
  currentRowIndex = 4;
  activeTotalRowIndex = undefined;
  realizedTotalRowIndex = undefined;
};

// started row 
let currentRowIndex = 4;
let activeTotalRowIndex: number;
let realizedTotalRowIndex: number;

export const getSumForSubTotal = (letter: string, startIndex: number, endIndex: number) => {
  return `SUM(${letter}${startIndex}:${letter}${endIndex})`;
};

const getMoicForSubTotal = (letterStart: string, letterEnd: string, subTotalIndex) => {
  return `IFERROR(${letterStart}${subTotalIndex}/${letterEnd}${subTotalIndex},0)`;
};

const getSumForGrandTotal = (letter: string) => {
  return `SUM(${letter}${activeTotalRowIndex},${letter}${realizedTotalRowIndex})`;
};

const getDiffFormuaForDelta = (letterStart: string, letterEnd: string) => {
  return `${letterStart}${currentRowIndex}-${letterEnd}${currentRowIndex}`
}
